import { useState } from 'react';
import {
  ArrowRight,
  BadgeDollarSign,
  Building2,
  ChevronRight,
  CircleDollarSign,
  ClipboardList,
  FileText,
  HandCoins,
  LifeBuoy,
  Mail,
  MessageCircle,
  MessageSquareMore,
  Users,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export default function ChatButton() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [userType, setUserType] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  const merchantOptions = [
    {
      id: 'payoff',
      label: 'Payoff Letter',
      action: 'Email funding@bizfund.com',
      icon: FileText,
      href: 'mailto:funding@bizfund.com',
    },
    {
      id: 'funding',
      label: 'More Funding',
      action: 'Email your last 3 months of statements to accounting@bizfund.com',
      emailAddress: 'accounting@bizfund.com',
      icon: BadgeDollarSign,
      href: 'mailto:accounting@bizfund.com',
    },
    {
      id: 'payments',
      label: 'Lower Payments',
      action: 'Email your last 3 months of statements to funding@bizfund.com',
      icon: HandCoins,
      href: 'mailto:funding@bizfund.com',
    },
    {
      id: 'balance',
      label: 'Current Balance',
      action: 'Email funding@bizfund.com',
      icon: CircleDollarSign,
      href: 'mailto:funding@bizfund.com',
    },
    {
      id: 'other',
      label: 'Other Questions',
      action: 'Contact form',
      icon: MessageSquareMore,
      to: '/contact',
    },
  ];

  const partnerOptions = [
    {
      id: 'register',
      label: 'Register as Partner',
      action: 'Opens Partner form',
      icon: Users,
      to: '/partner#complete-registration',
    },
    {
      id: 'status',
      label: 'Check Deal Status',
      action: 'Opens Partner Portal',
      icon: ClipboardList,
      to: '/login',
    },
    {
      id: 'support',
      label: 'Need Support?',
      action: 'Contact form',
      icon: LifeBuoy,
      to: '/contact',
    },
  ];

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleClose = () => {
    setIsOpen(false);
    setUserType(null);
    setSelectedOption(null);
  };

  const handleAction = (option) => {
    if (option.href) {
      window.location.href = option.href;
      handleClose();
      return;
    }

    if (option.to) {
      if (option.to.includes('#')) {
        window.location.href = option.to;
        handleClose();
        return;
      }

      navigate(option.to);
      handleClose();
    }
  };

  const userTypeButtonClass =
    'group flex w-full items-center justify-between rounded-2xl border px-5 py-4 transition-all duration-300 shadow-[0_16px_36px_rgba(2,6,23,0.18)]';

  const optionCardClass =
    'group w-full rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-4 text-left text-sm text-slate-100 transition-all duration-300 hover:border-amber-300/40 hover:bg-white/[0.1] hover:shadow-[0_16px_36px_rgba(2,6,23,0.18)]';

  const renderContent = () => {
    if (!userType) {
      return (
        <div className="space-y-3">
          <motion.button
            key="merchant"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => setUserType('merchant')}
            className={`${userTypeButtonClass} border-blue-400/30 bg-gradient-to-br from-blue-600 to-blue-700 text-white hover:-translate-y-0.5 hover:from-blue-500 hover:to-blue-700`}
          >
            <span className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10">
                <Building2 size={18} className="transition-transform group-hover:scale-110" />
              </span>
              I'm a Merchant
            </span>
            <ArrowRight size={18} className="text-white/90 transition-transform group-hover:translate-x-1" />
          </motion.button>

          <motion.button
            key="partner"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            onClick={() => setUserType('partner')}
            className={`${userTypeButtonClass} border-white/12 bg-white text-slate-900 hover:-translate-y-0.5 hover:bg-slate-100`}
          >
            <span className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-100">
                <Users size={18} className="transition-transform group-hover:scale-110" />
              </span>
              I'm a Partner
            </span>
            <ArrowRight size={18} className="text-slate-500 transition-transform group-hover:translate-x-1" />
          </motion.button>
        </div>
      );
    }

    const options = userType === 'merchant' ? merchantOptions : partnerOptions;

    return (
      <div className="space-y-2.5">
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setUserType(null)}
          className="mb-3 flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors hover:text-white"
        >
          <ChevronRight size={16} className="rotate-180" /> Back
        </motion.button>

        {options.map((option, idx) => (
          <motion.button
            key={option.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.05 }}
            onClick={() => handleOptionSelect(option)}
            className={optionCardClass}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-1 items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/10 text-amber-300">
                  <option.icon size={16} className="transition-transform duration-300 group-hover:scale-110" />
                </span>
                <span className="flex-1 pt-1 font-medium text-slate-100 transition-colors group-hover:text-white">
                  {option.label}
                </span>
              </div>
              <ChevronRight
                size={16}
                className="mt-3 shrink-0 text-slate-500 transition-all group-hover:translate-x-1 group-hover:text-amber-300"
              />
            </div>
          </motion.button>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed bottom-6 right-6 z-40">
      <AnimatePresence mode="wait">
        {!isOpen ? (
          <motion.button
            key="button"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="group flex items-center justify-center gap-2.5 rounded-full bg-[#0f172a] px-6 py-4 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(2,6,23,0.32)] transition-all hover:bg-[#162033] hover:shadow-[0_22px_55px_rgba(2,6,23,0.4)]"
            aria-label="Open chat"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-400 text-slate-950">
              <MessageCircle size={18} className="transition-transform group-hover:scale-110" />
            </span>
            <span className="hidden sm:inline">Chat with us</span>
          </motion.button>
        ) : (
          <motion.div
            key="panel"
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 350, damping: 35 }}
            className="w-80 max-h-[500px] overflow-y-auto rounded-[28px] border border-white/8 bg-[#0f172a] p-6 text-white shadow-2xl"
            style={{
              boxShadow: '0 28px 70px rgba(2, 6, 23, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.04)',
            }}
          >
            <div className="mb-6 flex items-center justify-between">
              <div>
                <div className="mb-3 inline-flex rounded-full border border-amber-400/20 bg-amber-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-300">
                  Bizfund Support
                </div>
                <h3 className="text-xl font-bold text-white">
                  {!userType ? '👋 How can we help?' : userType === 'merchant' ? '🏢 Merchant Support' : '🤝 Partner Support'}
                </h3>
                <p className="mt-1 text-xs text-slate-400">Quick answers, zero wait time</p>
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                onClick={handleClose}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Close chat"
              >
                <X size={20} />
              </motion.button>
            </div>

            {selectedOption ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <motion.button
                  whileHover={{ x: -2 }}
                  onClick={() => setSelectedOption(null)}
                  className="flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors hover:text-white"
                >
                  <ChevronRight size={16} className="rotate-180" /> Back
                </motion.button>

                <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/8 to-white/[0.04] p-5 shadow-[0_18px_40px_rgba(2,6,23,0.18)]">
                  <p className="mb-3 flex items-center gap-2 font-semibold text-white">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-300/20 bg-amber-400/10 text-amber-300">
                      <selectedOption.icon size={16} />
                    </span>
                    {selectedOption.to || selectedOption.href ? (
                      <button
                        type="button"
                        onClick={() => handleAction(selectedOption)}
                        className="text-left underline decoration-white/40 underline-offset-4 transition-colors hover:text-amber-300 hover:decoration-amber-300"
                      >
                        {selectedOption.label}
                      </button>
                    ) : (
                      selectedOption.label
                    )}
                  </p>
                  <p className="flex items-center gap-2 text-sm leading-relaxed text-slate-300">
                    <Mail size={14} className="shrink-0 text-amber-300" />
                    {selectedOption.emailAddress ? (
                      <span>
                        {selectedOption.action}{' '}
                        <a
                          href={selectedOption.href}
                          onClick={(e) => e.stopPropagation()}
                          className="font-semibold text-amber-300 underline decoration-amber-300/50 underline-offset-4 transition-colors hover:text-amber-200"
                        >
                          {selectedOption.emailAddress}
                        </a>
                      </span>
                    ) : (
                      selectedOption.action
                    )}
                  </p>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAction(selectedOption)}
                  className="w-full rounded-xl bg-white py-3 font-semibold text-slate-900 transition-all hover:bg-slate-100 hover:shadow-lg"
                >
                  Email
                </motion.button>
              </motion.div>
            ) : (
              renderContent()
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
